// ─────────────────────────────────────────────────────────────────────────────
// FindCareIcons.jsx
// One <Icon name="..." /> component, one flat outline SVG style throughout.
// Add new icons by adding a <path>/shape set to ICON_PATHS — nothing else
// in the flow needs to change since every screen references icons by name
// from findCareContent.json.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";

const ICON_PATHS = {
  stethoscope: (
    <>
      <path d="M6 3v6a4 4 0 0 0 8 0V3" />
      <path d="M18 3v4a2 2 0 1 1-4 0" />
      <circle cx="19" cy="16.5" r="2.5" />
      <path d="M10 13v2a5 5 0 0 0 5 5" />
    </>
  ),
  doctorChat: (
    <>
      <circle cx="12" cy="7" r="3" />
      <path d="M5 21v-1a7 7 0 0 1 14 0v1" />
      <path d="M15 3.5c1.5.6 2.5 2 2.5 3.5" />
    </>
  ),
  flask: (
    <>
      <path d="M9 3h6" />
      <path d="M10 3v6l-5 9.5A2 2 0 0 0 6.7 21h10.6a2 2 0 0 0 1.7-2.5L14 9V3" />
      <path d="M8 15h8" />
    </>
  ),
  heartPulse: (
    <>
      <path d="M12 20s-7-4.35-9.5-8.8C1 8.2 2.6 5 6 5c2 0 3.3 1.1 4 2.3.7-1.2 2-2.3 4-2.3 3.4 0 5 3.2 3.5 6.2C19 15.65 12 20 12 20Z" />
      <path d="M7 12h2l1.5-2.5L12 15l1.5-3H17" />
    </>
  ),
  teen: (
    <>
      <circle cx="12" cy="6" r="3" />
      <path d="M6 21v-3a6 6 0 0 1 12 0v3" />
    </>
  ),
  fertility: (
    <>
      <path d="M12 20.5S4.5 15.8 4.5 10a4.5 4.5 0 0 1 7.5-3.4A4.5 4.5 0 0 1 19.5 10c0 5.8-7.5 10.5-7.5 10.5Z" transform="translate(-2 -1) scale(0.85)" />
      <path d="M12 20.5S4.5 15.8 4.5 10a4.5 4.5 0 0 1 7.5-3.4A4.5 4.5 0 0 1 19.5 10c0 5.8-7.5 10.5-7.5 10.5Z" transform="translate(3 -1) scale(0.85)" />
    </>
  ),
  pregnancy: (
    <>
      <circle cx="13" cy="5" r="2.2" />
      <path d="M9 21v-6c0-3 1.8-6 4-6.6" />
      <path d="M13 8.4c3 .3 5 2.9 5 6.1v1" />
      <path d="M9 15h3" />
    </>
  ),
  postpartum: (
    <>
      <circle cx="9" cy="5" r="2" />
      <path d="M9 7v5" />
      <circle cx="15" cy="14.5" r="1.6" />
      <path d="M6 21c0-4 1.5-7 3-8.3" />
      <path d="M15 16v5" />
    </>
  ),
  sprout: (
    <>
      <path d="M12 21v-9" />
      <path d="M12 12C12 8 9 6 5.5 6.5 5.5 10.5 8 12.5 12 12Z" />
      <path d="M12 12c0-3.5 2.5-5.5 6.5-5-0.3 3.7-2.7 5.7-6.5 5Z" />
      <path d="M8.5 21h7" />
    </>
  ),
  bloom: (
    <>
      <circle cx="12" cy="12" r="2" />
      <path d="M12 4c1.4 0 2.5 1.4 2.5 3s-1.1 3-2.5 3-2.5-1.4-2.5-3S10.6 4 12 4Z" />
      <path d="M12 14c1.4 0 2.5 1.4 2.5 3s-1.1 3-2.5 3-2.5-1.4-2.5-3 1.1-3 2.5-3Z" />
      <path d="M4 12c0-1.4 1.4-2.5 3-2.5s3 1.1 3 2.5-1.4 2.5-3 2.5S4 13.4 4 12Z" />
      <path d="M14 12c0-1.4 1.4-2.5 3-2.5s3 1.1 3 2.5-1.4 2.5-3 2.5-3-1.1-3-2.5Z" />
    </>
  ),
  lotus: (
    <>
      <path d="M12 21c-4-1-6-4-6-7 2 1 4 1 6 0 2 1 4 1 6 0 0 3-2 6-6 7Z" />
      <path d="M12 14V6" />
      <path d="M12 6c-1.5-1.5-1.5-3.5 0-5 1.5 1.5 1.5 3.5 0 5Z" />
      <path d="M12 9c-2-1-3.5-1-5 0 1 1.6 3 2 5 1.5" />
      <path d="M12 9c2-1 3.5-1 5 0-1 1.6-3 2-5 1.5" />
    </>
  ),
  clinic: (
    <>
      <path d="M4 21V9l8-5 8 5v12" />
      <path d="M9 21v-6h6v6" />
      <path d="M12 9v3M10.5 10.5h3" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="6" width="12" height="12" rx="2" />
      <path d="M15 10l6-3v10l-6-3" />
    </>
  ),
  phone: (
    <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20a1 1 0 0 1-1 1C11.6 21 3 12.4 3 2a1 1 0 0 1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1L6.6 10.8Z" />
  ),
  headset: (
    <>
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
      <rect x="3" y="13" width="4" height="6" rx="1.5" />
      <rect x="17" y="13" width="4" height="6" rx="1.5" />
      <path d="M19 19v1a3 3 0 0 1-3 3h-2" />
    </>
  ),
  scan: (
    <>
      <path d="M4 8V5a1 1 0 0 1 1-1h3M20 8V5a1 1 0 0 0-1-1h-3M4 16v3a1 1 0 0 0 1 1h3M20 16v3a1 1 0 0 1-1 1h-3" />
      <path d="M12 8l3 4-3 4-3-4 3-4Z" />
    </>
  ),
  checkShield: (
    <>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  vaccine: (
    <>
      <path d="M18 3l3 3-2 2-3-3 2-2Z" />
      <path d="M17 6L6 17v3H3v-3l11-11" />
      <path d="M9 12l3 3" />
    </>
  ),
  ribbon: (
    <>
      <circle cx="12" cy="7" r="4" />
      <path d="M9.5 10.5L7 21l5-3 5 3-2.5-10.5" />
    </>
  ),
  flower: (
    <>
      <circle cx="12" cy="12" r="2" />
      <path d="M12 4a3 3 0 0 1 0 6 3 3 0 0 1 0-6ZM12 14a3 3 0 0 1 0 6 3 3 0 0 1 0-6ZM4 12a3 3 0 0 1 6 0 3 3 0 0 1-6 0ZM14 12a3 3 0 0 1 6 0 3 3 0 0 1-6 0Z" />
    </>
  ),
  thyroid: (
    <>
      <path d="M8 10c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5" />
      <path d="M6 10c-1.2.7-2 2-2 3.5C4 16.5 6.5 19 9.5 19h5c3 0 5.5-2.5 5.5-5.5 0-1.5-.8-2.8-2-3.5" />
    </>
  ),
  droplet: (
    <path d="M12 3s6 6.5 6 10.5A6 6 0 0 1 6 13.5C6 9.5 12 3 12 3Z" />
  ),
  mind: (
    <>
      <path d="M9 3a4 4 0 0 0-4 4v1a3 3 0 0 0 0 6v1a4 4 0 0 0 4 4" />
      <path d="M15 3a4 4 0 0 1 4 4v1a3 3 0 0 1 0 6v1a4 4 0 0 1-4 4" />
      <path d="M9 3v18M15 3v18" />
    </>
  ),
  moreDots: (
    <>
      <circle cx="6" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="18" cy="12" r="1.4" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 21v-1a7 7 0 0 1 14 0v1" />
    </>
  ),
  surgery: (
    <>
      <path d="M6 3l7 7" />
      <circle cx="5" cy="19" r="2" />
      <circle cx="19" cy="19" r="2" />
      <path d="M13 10L6.5 17.5M13 10l6.5 7.5" />
    </>
  ),
  clinicCross: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  sparkle: (
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" />
      <circle cx="12" cy="9.5" r="2.3" />
    </>
  ),
  crosshair: (
    <>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    </>
  ),
  chevronDown: <path d="M6 9l6 6 6-6" />,
  chevronLeft: <path d="M15 18l-6-6 6-6" />,
  chevronRight: <path d="M9 18l6-6-6-6" />,
  check: <path d="M4.5 12.5l5 5L19.5 7" />,
  editPencil: (
    <path d="M4 20l1-4.5L15.5 5 19 8.5 8.5 19 4 20Z" />
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
};

/**
 * <Icon name="stethoscope" size={20} color={T.purple700} strokeWidth={1.8} />
 * Every path set is unfilled/outline by default (stroke = color, fill = none),
 * so a single component covers every icon used across the Find Care flow.
 */
export default function Icon({ name, size = 20, color = "currentColor", strokeWidth = 1.7, style }) {
  const paths = ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}